import { useState, useEffect } from 'react';
import { EventCard } from '../components/EventCard';
import style from '../styles/GroupEvents.module.css';
import { trpc } from '../utils/trpc';
import { useParams, useNavigate } from 'react-router-dom';
import EditEventModal from './EditEventModal';
import { useAuth } from '../contexts/AuthContext';
import { EventInfo } from '../components/EventInfo';
import { useBannerStore } from '../utils/banner';
import GroupsIcon from '../images/GroupsIcon.png';

//Main component for displaying and managing events for a specific group
export function GroupEvents() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [showPastEvents, setShowPastEvents] = useState(false);
  const [editingEvent, setEditingEvent] = useState<any | null>(null);
  const [deletingEvent, setDeletingEvent] = useState<any | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const utils = trpc.useUtils();

  //gets the groupId from the URL parameters
  const { groupId } = useParams<{ groupId: string }>();

  // Pass navigation status to global banner state for render
  const showBanner = useBannerStore((state) => state.showBanner);
  const clearBanner = useBannerStore((state) => state.clearBanner);
  useEffect(() => {
    showBanner({
      icon: GroupsIcon,
      header: 'Group Events',
      buttons: {
        'Group Page': `/groups/${groupId}`,
      },
    });
    return () => clearBanner();
  }, []);

  //fetch events for the group
  const { data: events = [], isLoading: eventsLoading } =
    trpc.event.getEventsByGroupId.useQuery(
      { groupId: groupId ?? '', upcomingOnly: !showPastEvents },
      { enabled: !!groupId },
    );

  //fetch group members to determine user roles
  const { data: members = [] } = trpc.group.getMembers.useQuery(
    { groupId: groupId ?? '' },
    { enabled: !!groupId },
  );

  //determine current user roles in the group
  const currentUserRole = members.find((m: any) => m.profile_id === user?.id)?.role;
  //checks if the user has permissions to create events based on their role in the group
  const canCreateEvent = ['owner', 'admin', 'moderator'].includes(
    currentUserRole as string,
  );

  //mutation for deleting an event
  const deleteEventMutation = trpc.event.cancelEvent.useMutation();

  //handle delete confirmation, calls the delete mutation and then refetches the event list for the group to reflect the deletion
  const handleConfirmDelete = async () => {
    if (!deletingEvent) return;
    try {
      await deleteEventMutation.mutateAsync({ eventId: deletingEvent.event_id });
      setDeletingEvent(null);
      // Invalidate the events query for this group to refetch updated list
      if (groupId) {
        await utils.event.getEventsByGroupId.invalidate({
          groupId,
          upcomingOnly: !showPastEvents,
        });
      }
    } catch (err) {
      console.error('Failed to delete event:', err);
    }
  };

  //filters based on search term and status filter
  const filteredEvents = events.filter(
    (event: any) =>
      event.event_name.toLowerCase().includes(searchTerm.toLowerCase()) &&
      (statusFilter === 'all' ||
        event.status.toLowerCase() === statusFilter)
  );

  //shows loading state while fetching events
  if (eventsLoading) {
    return <div className={style.container}>Loading events...</div>;
  }
  return (
    <div className={style.container}>
      <div className={style.headerRow}>
        {/*Page title*/}
        <h1 className={style.title}>Events</h1>

        {/* Tabs for toggling between upcoming events and all events */}
        <div className={style.tabs}>
          <button
            className={`${style.tab} ${!showPastEvents ? style.activeTab : ''}`}
            onClick={() => setShowPastEvents(false)}
          >
            Upcoming Events
          </button>

          <button
            className={`${style.tab} ${showPastEvents ? style.activeTab : ''}`}
            onClick={() => setShowPastEvents(true)}
          >
            Past Events
          </button>
        </div>

        {/*Show create event button if user has permissions to create events in the group*/}
        {canCreateEvent && (
          <button
            onClick={() => navigate(`/groups/${groupId}/create-event`)}
            className={style.createEventBtn}
          >
            Create Event
          </button>
        )}
      </div>

      {/*Search bar for filtering events by name*/}
      <div className={style.searchBar}>
        <input
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      <div className={style.filterRow}>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className={style.filterDropdown}
        >
          <option value="all">All Statuses</option>
          <option value="accepted">Accepted</option>
          <option value="pending">Pending</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {/*Event list*/}
      <div className={style.feedColumn}>
        {filteredEvents.length === 0 ? (
          <p>No events found.</p>
        ) : (
          filteredEvents.map((event: any, i: number) => (
            <EventCard
              key={event.id}
              event={event}
              groupId={groupId ?? ''}
              animationDelay={`${0.07 * i}s`}
              onEdit={(event) => setEditingEvent(event)}
              onDelete={(event) => setDeletingEvent(event)}
              onSelect={(id: string) => setSelectedEventId(id)}
            />
          ))
        )}
      </div>
      {/*Edit event modal*/}
      {editingEvent && (
        <EditEventModal
          isOpen={true}
          event={editingEvent}
          groupId={groupId ?? ''}
          onClose={() => setEditingEvent(null)}
        />
      )}
      {/*Delete confirmation modal*/}
      {deletingEvent && (
        <div className={style.modalOverlay}>
          <div className={style.modalContent}>
            <h2>Confirm Delete</h2>
            {/*Confirmation message*/}
            <p>Are you sure you want to delete "{deletingEvent.event_name}"?</p>
            <div className={style.modalButtons}>
              <button className={style.cancelBtn} onClick={() => setDeletingEvent(null)}>
                Cancel
              </button>
              <button className={style.confirmBtn} onClick={handleConfirmDelete}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
      {/*Event info modal*/}
      {selectedEventId && (
        <EventInfo id={selectedEventId} onClose={() => setSelectedEventId(null)} />
      )}
    </div>
  );
}
